import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Navbar from './Navbar';
import Footer from './Footer';
import { getAllStates, getAllCitiesForState, citiesData, getCityUrl } from '../data/citiesData';
import { trackPageView } from '../utils/tracking';

function OurPresencePage() {
  const [expandedState, setExpandedState] = useState(null);
  const [trackingInitialized, setTrackingInitialized] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!trackingInitialized) {
      trackPageView('/our-presence', 'Our Presence Page');
      setTrackingInitialized(true);
    }
  }, [trackingInitialized]);

  const states = getAllStates();

  const toggleState = (stateName) => {
    setExpandedState(expandedState === stateName ? null : stateName);
  };

  const getCitiesForState = (stateName) => {
    return getAllCitiesForState(stateName);
  };

  const getStateDisplayInfo = (stateName) => {
    const state = citiesData[stateName];
    if (!state) return { count: 0 };
    return { count: state.cities?.length || 0 };
  };

  return (
    <>
      <Helmet>
        <title>Our Presence | Solar Panel Installation Services Across India | Lunar Electric</title>
        <meta
          name="description"
          content="Lunar Electric provides solar panel installation services across major cities in India including Bangalore, Mumbai, Delhi, Chennai, Hyderabad, Pune, and more. Find solar solutions in your city."
        />
        <meta
          name="keywords"
          content="solar panel installation cities, solar services India, solar Bangalore, solar Mumbai, solar Delhi, solar Chennai, solar Hyderabad, solar installation locations, Lunar Electric presence, solar services near me"
        />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta property="og:title" content="Our Presence | Solar Panel Installation Services Across India" />
        <meta
          property="og:description"
          content="Lunar Electric provides solar panel installation services across major cities in India. Find solar solutions in your city."
        />
        <meta property="og:url" content="https://lunarelectric.in/our-presence" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Our Presence | Solar Panel Installation Services Across India" />
        <meta
          name="twitter:description"
          content="Lunar Electric provides solar panel installation services across major cities in India."
        />
        <link rel="canonical" href="https://lunarelectric.in/our-presence" />
      </Helmet>

      <a href="#main-content" className="skip-to-main" style={{position: 'absolute', left: '-9999px', zIndex: 9999}} onFocus={(e) => e.target.style.left = '0'}>
        Skip to main content
      </a>

      <div className="our-presence-page" id="main-content">
        <Navbar />

        {/* Hero Section */}
        <section className="presence-hero">
          <div className="container">
            <div className="hero-content">
              <h1>Our Presence Across India</h1>
              <p className="hero-subtitle">
                Bringing Solar Energy Solutions to Your City
              </p>
              <p className="hero-description">
                Lunar Electric provides professional solar panel installation services across major cities in India. 
                From tier 1 metros to tier 3 cities, we're expanding our reach to make solar energy accessible to everyone.
              </p>
            </div>
          </div>
        </section>

        {/* States Section */}
        <section className="presence-states-section">
          <div className="container">
            <h2 className="section-title">Select Your State</h2>
            <p className="section-subtitle">
              Click on a state to see all cities where we provide solar installation services
            </p>

            <div className="states-grid">
              {states.map((stateName) => {
                const isExpanded = expandedState === stateName;
                const cities = getCitiesForState(stateName);
                const stateInfo = getStateDisplayInfo(stateName);
                
                return (
                  <div key={stateName} className="state-card">
                    <button
                      className={`state-button ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => toggleState(stateName)}
                      aria-expanded={isExpanded}
                      aria-controls={`cities-${stateName}`}
                    >
                      <span className="state-name">{stateName}</span>
                      <span className="state-badge">
                        {cities.length} {cities.length === 1 ? 'City' : 'Cities'}
                      </span>
                      <span className="expand-icon" aria-hidden="true">
                        {isExpanded ? '−' : '+'}
                      </span>
                    </button>
                    
                    {isExpanded && (
                      <div
                        id={`cities-${stateName}`}
                        className="cities-dropdown"
                        role="region"
                        aria-label={`Cities in ${stateName}`}
                      >
                        <div className="cities-list">
                          {cities.map((city) => (
                            <Link
                              key={city}
                              to={getCityUrl(city)}
                              className="city-link"
                            >
                              {city}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="presence-cta-section">
          <div className="container">
            <div className="cta-content">
              <h2>Ready to Go Solar?</h2>
              <p>Get a free quote for solar panel installation in your city</p>
              <div className="cta-buttons">
                <Link to="/callback" className="btn-primary">
                  Get Free Quote
                </Link>
                <Link to="/solar-calculator" className="btn-secondary">
                  Calculate Savings
                </Link>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}

export default OurPresencePage;

